import {
    usePaginatedWorkflowExecutions,
    type PlainWorkflow,
    type WorkflowExecutionsFilter,
} from "@/queries/automation-queries";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useDebounce } from "use-debounce";
import { Link } from "@/components/flexkit/Link";
import { WorkflowExecutionsCard } from "@/components/automations/AutomationExecutionCard";
import { workflowExecutionsUrl, isWorkflowDeleting } from "@/utils/automations";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { Button } from "@/components/flexkit/Button";
import { Command, CommandEmpty, CommandInput, CommandList } from "@/components/flexkit/Command";
import { Command as CommandPrimitive } from "cmdk";
import { standaloneInputClassName } from "@/components/podkit/forms/input-classes";
import { cn } from "@/components/podkit/lib/cn";
import {
    WorkflowExecutionStatusFilter,
    WorkflowExecutionStatusFilterValue,
} from "@/components/automations/AutomationExecutionStatusFilter";
import { WorkflowExecutionPhase } from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { OrganizationMember } from "gitpod-next-api/gitpod/v1/organization_pb";
import { formatError } from "@/utils/errors";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { RunAutomationButton } from "@/routes/automations/automation-details-page/RunAutomationButton";
import { AdaptivePagination } from "@/components/flexkit/AdaptivePagination";
import { parseAsInteger, useQueryState } from "nuqs";

/**
 * Maps the UI status filter to API filter parameters
 */
function statusFilterToApiFilter(
    statusFilter: WorkflowExecutionStatusFilterValue,
): WorkflowExecutionsFilter | undefined {
    switch (statusFilter) {
        case WorkflowExecutionStatusFilterValue.Running:
            return {
                statusPhases: [WorkflowExecutionPhase.RUNNING],
            };
        case WorkflowExecutionStatusFilterValue.Completed:
            return {
                statusPhases: [WorkflowExecutionPhase.COMPLETED],
                hasFailedActions: false,
            };
        case WorkflowExecutionStatusFilterValue.Failed:
            return {
                statusPhases: [WorkflowExecutionPhase.COMPLETED],
                hasFailedActions: true,
            };
        case WorkflowExecutionStatusFilterValue.All:
        default:
            return undefined;
    }
}

type WorkflowExecutionsListProps = {
    members?: PlainMessage<OrganizationMember>[];
    workflow?: PlainWorkflow;
};

export const WorkflowExecutionsList: FC<WorkflowExecutionsListProps> = ({ workflow, members }) => {
    const [searchValue, setSearchValue] = useState<string>("");
    const [debouncedSearchValue] = useDebounce(searchValue, 300);
    const [statusFilter, setStatusFilter] = useState<WorkflowExecutionStatusFilterValue>(
        WorkflowExecutionStatusFilterValue.All,
    );

    // Page-based pagination state (1-based in URL, converted to 0-based for API)
    const [currentPage, setCurrentPage] = useQueryState("page", parseAsInteger.withDefault(1));

    const onStatusFilterChange = useCallback(
        async (filter: WorkflowExecutionStatusFilterValue) => {
            await setCurrentPage(1);
            setStatusFilter(filter);
        },
        [setCurrentPage, setStatusFilter],
    );

    // Reset to first page when search changes
    useEffect(() => {
        void setCurrentPage(1);
    }, [debouncedSearchValue, setCurrentPage]);

    const apiFilter = useMemo((): WorkflowExecutionsFilter | undefined => {
        const baseFilter = statusFilterToApiFilter(statusFilter);
        const search = debouncedSearchValue.trim();

        if (!baseFilter && !search) {
            return undefined;
        }

        return {
            ...baseFilter,
            search: search || undefined,
        };
    }, [statusFilter, debouncedSearchValue]);

    const {
        data: executions,
        error,
        refetch,
        isPending,
        isFetching,
        totalCount,
        totalPages,
        pageSize,
    } = usePaginatedWorkflowExecutions({
        enabled: !!workflow?.id,
        workflowId: workflow?.id || "",
        filter: apiFilter,
        page: currentPage - 1,
    });

    const isDeleting = isWorkflowDeleting(workflow);

    if (error) {
        return (
            <EmptyStateCard
                title="Failed to load executions"
                description={formatError(error)}
                data-testid="workflow-executions-error-state"
            >
                {refetch && (
                    <Button
                        variant="secondary"
                        LeadingIcon={IconRefresh}
                        onClick={() => refetch()}
                        data-tracking-id="retry-load-executions"
                    >
                        Retry
                    </Button>
                )}
            </EmptyStateCard>
        );
    }

    const isFiltered = statusFilter !== WorkflowExecutionStatusFilterValue.All || !!debouncedSearchValue.trim();

    if (executions.length === 0 && !isFiltered) {
        return (
            <EmptyStateCard
                title="No executions yet"
                description="Run this automation to create a new execution. It will run all defined steps across your configured contexts and report progress as it completes."
                data-testid="workflow-executions-empty-state"
            >
                {workflow && (
                    <RunAutomationButton
                        workflow={workflow}
                        disabled={isDeleting}
                        data-tracking-id="run-workflow-empty-state"
                    >
                        Run Automation
                    </RunAutomationButton>
                )}
            </EmptyStateCard>
        );
    }

    return (
        <div>
            <Command
                className="flex-col gap-3 overflow-visible border-none bg-transparent shadow-none"
                data-testid="workflow-executions-list"
                shouldFilter={false}
            >
                <div className="flex items-center justify-between gap-3">
                    <CommandInput
                        placeholder="Search executions"
                        value={searchValue}
                        onValueChange={setSearchValue}
                        containerClassName={cn(
                            standaloneInputClassName,
                            "max-w-none *:p-0",
                            "read-only:bg-inherit read-only:cursor-auto read-only:text-inherit",
                        )}
                    />
                    <WorkflowExecutionStatusFilter value={statusFilter} onChange={onStatusFilterChange} />
                </div>
                {!isPending && (
                    <CommandList defaultChecked={false} className="px-1 py-1 *:flex *:flex-col *:gap-2">
                        <CommandEmpty className="w-full text-center text-base text-content-muted">
                            No executions found
                            {searchValue && (
                                <>
                                    {" "}
                                    matching <span className="font-medium text-content-strong">{searchValue}</span>
                                </>
                            )}
                        </CommandEmpty>
                        {executions.map((execution) => (
                            <CommandPrimitive.Item key={execution.id} value={execution.id} className="group" asChild>
                                <Link to={workflowExecutionsUrl(execution)} className="flex-1">
                                    <WorkflowExecutionsCard execution={execution} members={members} />
                                </Link>
                            </CommandPrimitive.Item>
                        ))}
                    </CommandList>
                )}
            </Command>
            <AdaptivePagination
                className="px-2 pb-0 pt-2 sm:px-2"
                totalCount={totalCount}
                totalPages={totalPages}
                currentPage={currentPage - 1}
                onPageChange={(page) => {
                    void setCurrentPage(page + 1);
                }}
                isLoading={isFetching}
                currentItemsCount={executions.length}
                pageSize={pageSize}
            />
        </div>
    );
};
